import { NetworkConnectionType, NetworkService } from "~/app/core/network.service";
import { StorageService } from "~/app/core/storage.service";
import { Settings } from "~/app/core/model/settings";
import { SettingsService } from "~/app/core/settings.service";
import { RPCService } from "~/app/core/rpc.service";
import { InterfaceTicketService } from "~/app/core/model/ticket-service/interface-ticket-service";
import { momentNow, toDateStr, toMoment } from "~/app/core/model/date-str";
import { TicketInfo } from "~/app/core/model/ticket-info";

export class HttpTicketService implements InterfaceTicketService {
    constructor(
        private readonly networkService: NetworkService,
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService,
        private readonly rpcService: RPCService
    ) {
    }

    private get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    fetchTickets(): Promise<void> {
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            // fetch tickets
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsFetch().then((ticketInfo) => {
                    this.updateActualTickets(ticketInfo);
                    resolve();
                }, (error) => reject(error));
            });
        } else {
            // don't fetch tickets, just reset daily tasks
            const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
            if (!actual.dailyTasks || !toMoment(actual.dailyTasks.date).isSame(momentNow(), "date")) {
                actual.dailyTasks = { date: toDateStr(momentNow()) };
                this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
            }
            return Promise.resolve();
        }
    }

    earnTickets(action: "openApp"|"openOffer"|"share"|"filter"|"webVisit"|"favorite"): Promise<void> {
        let earned: number = 0;
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        switch (action) {
            case "openApp":
                if (!actual.dailyTasks.openApp) {
                    actual.dailyTasks.openApp = true;
                    earned = this.settingsService.loadSettings().earnTicketsOpenApp;
                }
                break;
            case "openOffer":
                if (!actual.dailyTasks.openOffer) {
                    actual.dailyTasks.openOffer = true;
                    earned = this.settingsService.loadSettings().earnTicketsOpenOffer;
                }
                break;
            case "share":
                if (!actual.dailyTasks.share) {
                    actual.dailyTasks.share = true;
                    earned = this.settingsService.loadSettings().earnTicketsShare;
                }
                break;
            case "filter":
                if (!actual.dailyTasks.filterOffers) {
                    actual.dailyTasks.filterOffers = true;
                    earned = this.settingsService.loadSettings().earnTicketsFilterOffers;
                }
                break;
            case "webVisit":
                if (!actual.dailyTasks.webVisit) {
                    actual.dailyTasks.webVisit = true;
                    earned = this.settingsService.loadSettings().earnTicketsVisitWebsite;
                }
                break;
            case "favorite":
                if (!actual.dailyTasks.favorite) {
                    actual.dailyTasks.favorite = true;
                    earned = this.settingsService.loadSettings().earnTicketsFavoriteOffer;
                }
                break;
        }

        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            // immediately send message about complete task
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsActionEarned({ action: action, tickets: earned }).then((ticketInfo) => {
                    this.updateActualTickets(ticketInfo);
                    resolve();
                }, (error) => reject(error));
            });
        } else {
            // queue message about complete task and earned tickets
            this.rpcService.TicketsActionEarned_queue({ action: action, tickets: earned });
            actual.tickets += earned;
            this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
            return Promise.resolve();
        }
    }

    resetDailyTasks(): Promise<void> {
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            // reset daily tasks
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsResetDailyTasks().then((ticketInfo) => {
                    this.updateActualTickets(ticketInfo, true);
                    resolve();
                }, (error) => reject(error));
            });
        } else {
            return Promise.reject("No network");
        }
    }

    setTickets(ticketCount: number): Promise<void> {
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            // set tickets
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsSet(ticketCount).then((ticketInfo) => {
                    this.updateActualTickets(ticketInfo);
                    resolve();
                }, (error) => reject(error));
            });
        } else {
            return Promise.reject("No network");
        }
    }

    spendTickets(ticketCount: number): Promise<void> {
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            // immediately spend and update tickets
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsSpend(ticketCount).then((ticketInfo) => {
                    this.updateActualTickets(ticketInfo);
                    resolve();
                }, (error) => reject(error));
            });
        } else {
            // queue to update spent tickets
            this.rpcService.TicketsSpend_queue(ticketCount);
            const tickets = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
            tickets.tickets -= ticketCount;
            this.storageService.storeTicketInfoActual(tickets, this.settingsService.loadSettings().env);
            return Promise.resolve();
        }
    }

    verifyReferral(referralCode: string): Promise<boolean> {
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsVerifyReferral({ referralCode: referralCode, ticketsToReferrer: this.settings.earnTicketsSignUpFriend, ticketsToSignUp: this.settings.earnTicketsSignUpFromReferral }).then((success) => {
                    if (success) {
                        return this.fetchTickets().then(() => {
                            resolve(true);
                        }, (error) => reject(error));
                    } else {
                        resolve(false);
                    }
                }, (error) => reject(error));
            });
        } else {
            return Promise.reject("No network");
        }
    }

    earnTicketsFromRedeem(offerId: number, locationId: number, distance: number): Promise<void> {
        let earned: number = 0;
        const offer = this.storageService.loadOfferInfo(offerId);
        const nearby = distance !== null && distance <= this.settingsService.loadSettings().redeemNowMiles;
        if (offer) {
            if (offer.onlineOnly || !locationId || !nearby) {
                // no a specific offer location, so smaller reward
                earned = this.settingsService.loadSettings().earnTicketsRedeemOnline;
            } else {
                // specific offer location, so full reward
                earned = this.settingsService.loadSettings().earnTicketsRedeemLocal;
            }
        }
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            // immediately earn tickets
            return new Promise((resolve, reject) => {
                this.rpcService.TicketsRedeemEarned({ offer_id: offerId, location_id: locationId, tickets: earned }).then((ticketInfo) => {
                    this.updateActualTickets(ticketInfo);
                    resolve();
                }, (error) => reject(error));
            });
        } else {
            const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
            // queue message about earned tickets
            this.rpcService.TicketsRedeemEarned_queue({ offer_id: offerId, location_id: locationId, tickets: earned });
            actual.tickets += earned;
            this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
            return Promise.resolve();
        }
    }

    private updateActualTickets(ticketInfo: TicketInfo, resetNotified: boolean = false) {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        actual.tickets = ticketInfo.tickets;
        actual.dailyTasks = ticketInfo.dailyTasks;
        actual.referralCode = ticketInfo.referralCode;
        actual.referralEligible = ticketInfo.referralEligible;
        if (resetNotified || actual.referralNotified && !actual.referralEligible) actual.referralNotified = false;
        this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
    }
}
